import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutGrid,
  Briefcase,
  Users,
  Inbox,
  CalendarDays,
  Trophy,
  Heart,
  Building2,
  Bell,
  Settings,
  Menu,
  X,
  ChevronRight,
  Plus,
  Eye,
  BadgeCheck,
  Sparkles as MissionIcon,
  Users as NetworkIcon,
  MessageCircle,
  Compass,
} from "lucide-react";
import NetworkBadge from "@/components/NetworkBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/createurs", label: "Tableau de bord", icon: LayoutGrid, exact: true },
  { to: "/createurs/talents", label: "Talents", icon: Users },
  { to: "/createurs/annuaire", label: "Créateurs", icon: Compass },
  { to: "/createurs/collaborations", label: "Collaborations", icon: Briefcase },
  { to: "/events", label: "Événements", icon: CalendarDays },
  { to: "/reseau", label: "Réseau", icon: NetworkIcon, badge: "requests" },
  { to: "/messages", label: "Messages", icon: MessageCircle, badge: "messages" },
  { to: "/createurs/settings", label: "Paramètres", icon: Settings },
];

export default function BrandDashboardLayout({ children }) {
  const { user, profile, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (to, exact) => {
    if (exact) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  const brandName = profile?.company_name || user?.name || "Créateur";
  const initials = (brandName || "M")[0]?.toUpperCase();

  const publicProfileUrl = `${window.location.origin}/pros/${user?.user_id}`;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(publicProfileUrl);
      toast.success("Lien du profil copié !");
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="brand-layout">
      {/* Sidebar desktop — caramel warm */}
      <aside className="hidden lg:flex fixed top-0 left-0 h-screen w-64 flex-col bg-gradient-to-b from-[#C68E59] to-[#A26F3E] text-white z-30">
        <div className="px-6 pt-7 pb-5 border-b border-white/15">
          <Link to="/createurs" className="flex items-center gap-2.5 select-none" data-testid="brand-sidebar-logo" aria-label="Helencia Pro">
            <img src="/helencia-logo-256.png" alt="" width={36} height={36} className="rounded-xl" style={{ width: 36, height: 36 }} />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-[24px] font-medium tracking-tight text-white">Helencia</span>
              <span className="text-[9px] tracking-[0.25em] uppercase text-white/70 font-medium mt-0.5">Pro</span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to, item.exact);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-xl text-sm transition-all ${
                  active
                    ? "bg-white/95 text-[#1A1A1A] font-semibold shadow-sm"
                    : "text-white/80 hover:bg-white/15 hover:text-white"
                }`}
                data-testid={`brand-sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className={`w-4 h-4 ${active ? "text-[#C68E59]" : ""}`} />
                <span>{item.label}</span>
                {item.badge && <NetworkBadge kind={item.badge} dark={active} />}
                {active && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 m-3 rounded-2xl bg-[#1A1A1A]/35 border border-white/15 backdrop-blur text-white">
          <Plus className="w-5 h-5 text-[#E6C29A] mb-2" />
          <p className="font-serif text-lg leading-tight mb-1">Publier vite</p>
          <p className="text-xs text-white/75 leading-relaxed mb-3">
            Créez une collaboration et recevez des candidatures.
          </p>
          <Link to="/createurs/collaborations?new=1">
            <button className="w-full bg-white text-[#A26F3E] text-xs font-semibold tracking-wide rounded-full py-2 hover:bg-[#FAF3EA] transition">
              Publier une collaboration
            </button>
          </Link>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
          <aside className="fixed top-0 left-0 h-screen w-72 bg-gradient-to-b from-[#C68E59] to-[#A26F3E] text-white z-50 lg:hidden shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/15">
              <div className="flex items-center gap-2.5">
                <img src="/helencia-logo-256.png" alt="" width={32} height={32} className="rounded-lg" style={{ width: 32, height: 32 }} />
                <span className="font-serif text-2xl text-white">Helencia</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1 text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="p-3">
              {NAV.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to, item.exact);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 my-0.5 rounded-xl text-sm ${
                      active ? "bg-white/95 text-[#1A1A1A] font-semibold" : "text-white/85"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </>
      )}

      {/* Main */}
      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-[#FFFBF5]/85 backdrop-blur-xl border-b border-[#EADDC9]" data-testid="brand-topbar">
          <div className="px-5 sm:px-8 lg:px-10 h-16 flex items-center gap-3 sm:gap-4">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2" data-testid="brand-mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar className="h-9 w-9 border border-[#D4C3A5] flex-shrink-0">
                <AvatarImage src={profile?.logo_url || user?.picture} alt={brandName} />
                <AvatarFallback className="bg-[#F5E6D0] text-[#1A1A1A] font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex items-center gap-2 min-w-0">
                <p className="text-sm font-semibold text-[#1A1A1A] truncate">{brandName}</p>
                {profile?.verified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#F0F5FF] text-[#2563EB] border border-[#BFDBFE]">
                    <BadgeCheck className="w-2.5 h-2.5" />
                    Vérifié
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/createurs/${user?.user_id}`)}
                className="hidden md:inline-flex rounded-full border-[#D4C3A5] text-[#1A1A1A] hover:border-[#1A1A1A] h-9 px-3 text-xs font-semibold tracking-wide"
                data-testid="brand-top-public-profile"
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                Profil public
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/createurs/collaborations?new=1")}
                className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-9 px-4 text-xs font-semibold tracking-wide"
                data-testid="brand-top-new-collab"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Publier
              </Button>

              <NotificationBell listHref="/createurs/notifications" testId="brand-top-notifications-bell" />

              <DropdownMenu>
                <DropdownMenuTrigger className="focus:outline-none">
                  <Avatar className="h-9 w-9 border border-[#D4C3A5] cursor-pointer">
                    <AvatarImage src={profile?.logo_url || user?.picture} alt={brandName} />
                    <AvatarFallback className="bg-[#F5E6D0] text-[#1A1A1A] font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold">{brandName}</p>
                    <p className="text-xs text-[#8B8680]">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/createurs/${user?.user_id}`)}>
                    Profil public
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleShare}>
                    Partager le profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.open("/", "_blank")}>
                    Voir le site public
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/createurs/profile")}>
                    Éditer le profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/createurs/settings")}>
                    Paramètres
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>Déconnexion</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="px-5 sm:px-8 lg:px-10 py-8">{children}</main>

        {/* Mobile bottom nav — safe-area aware for iPhone X+ */}
        <div
          className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-[#EADDC9] z-30"
          data-testid="brand-bottom-nav"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="grid grid-cols-5 h-16">
            {[
              { to: "/createurs", icon: LayoutGrid, label: "Accueil", exact: true },
              { to: "/createurs/collaborations", icon: Briefcase, label: "Collabs" },
              { to: "/createurs/talents", icon: Users, label: "Talents" },
              { to: "/createurs/annuaire", icon: Compass, label: "Créateurs" },
              { to: "/createurs/favorites", icon: Heart, label: "Favoris" },
            ].map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to, item.exact);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex flex-col items-center justify-center gap-0.5 ${
                    active ? "text-[#1A1A1A]" : "text-[#8B8680]"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-[10px] font-semibold tracking-wide">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
        <div className="lg:hidden h-16" style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }} />
      </div>
    </div>
  );
}
